/**
 * Dorie Atelier — Liquid Glass Header + Announcement Bar System
 *
 * Responsibilities:
 *   1. On scroll past 80px, toggle .dorie-pill-active on #header-component.
 *   2. After the first frame, remove the .dorie-no-transition guard set by
 *      the inline pre-paint script in layout/theme.liquid.
 *   3. Inject a dismiss (×) button into the announcement bar and handle
 *      the spring-collapse dismiss. State persists in sessionStorage so
 *      the bar reappears on the next visit.
 *
 * What this script does NOT do:
 *   - It does not add the body class (dorie-homepage / dorie-inner). That is
 *     emitted by liquid in layout/theme.liquid so the right rules apply at
 *     first paint.
 *   - It does not apply the initial pill state at scrollY > 80. That is also
 *     done synchronously by an inline script in layout/theme.liquid, before
 *     the body becomes visible. This file just keeps it in sync as the user
 *     scrolls after load.
 *
 * Loaded with `defer` from the <head> in layout/theme.liquid, so it executes
 * after HTML parsing but before DOMContentLoaded.
 */
(function () {
  'use strict';

  var SCROLL_THRESHOLD = 80;
  var ticking = false;
  var DISMISS_KEY = 'dorie-announce-dismissed';

  // ─────────────────────────────────────────────
  // Header pill logic
  // ─────────────────────────────────────────────

  function applyState(header) {
    var y = window.scrollY || document.documentElement.scrollTop || 0;
    header.classList.toggle('dorie-pill-active', y > SCROLL_THRESHOLD);
    // Stage 2: once the user has scrolled ~1 page length, slim the pill ~50%.
    var slimThreshold = window.innerHeight || 800;
    header.classList.toggle('dorie-pill-slim', y > slimThreshold);
  }

  function onScroll(header) {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      applyState(header);
      ticking = false;
    });
  }

  // ─────────────────────────────────────────────
  // Announcement bar — glass dismiss button
  // ─────────────────────────────────────────────

  function initAnnouncementDismiss() {
    // Only run on the homepage — the bar is hidden via CSS on other pages
    // but we also skip the JS work to avoid unnecessary DOM queries.
    if (!document.body.classList.contains('dorie-homepage')) return;

    var bar = document.querySelector('aside.announcement-bar');
    if (!bar) return;

    // ── Kill the solid color-scheme fill ──
    // Atelier renders a .section-background div immediately before the <aside>
    // inside the header group — it carries the scheme's background-color and
    // paints through the glass (backdrop-filter blurs what's behind, so a solid
    // brown div behind the bar makes it look brown, not glass).
    // CSS has no previous-sibling selector so we handle this in JS.
    var parent = bar.parentElement;
    if (parent) {
      var bgs = parent.querySelectorAll('.section-background');
      for (var i = 0; i < bgs.length; i++) {
        bgs[i].style.display = 'none';
      }
    }
    // Also override the --color-background CSS variable directly on the bar
    // so the color-scheme class can't pull the scheme color back through.
    bar.style.setProperty('--color-background', 'transparent');

    // Already dismissed this session — collapse immediately (no animation)
    // so there's no flash of content before the transition would fire.
    if (sessionStorage.getItem(DISMISS_KEY) === '1') {
      bar.style.transition = 'none';
      bar.classList.add('dorie-bar-dismissed');
      // Re-enable transitions after a frame so subsequent interactions work.
      window.requestAnimationFrame(function () {
        bar.style.transition = '';
      });
      return;
    }

    // Build the dismiss button — thin × SVG, matching the Gentle Monster
    // reference. Two diagonal lines at 1.5px stroke, rounded caps.
    var btn = document.createElement('button');
    btn.className = 'dorie-announcement-dismiss';
    btn.setAttribute('type', 'button');
    btn.setAttribute('aria-label', 'Close announcement');
    btn.innerHTML =
      '<svg viewBox="0 0 12 12" aria-hidden="true">' +
        '<line x1="1" y1="1" x2="11" y2="11"/>' +
        '<line x1="11" y1="1" x2="1" y2="11"/>' +
      '</svg>';

    // The bar is position: relative so the button anchors to it correctly.
    bar.style.position = 'relative';
    bar.appendChild(btn);

    btn.addEventListener('click', function () {
      bar.classList.add('dorie-bar-dismissed');
      try {
        sessionStorage.setItem(DISMISS_KEY, '1');
      } catch (e) {
        // sessionStorage blocked (private mode / storage quota) — dismiss
        // visually but don't persist.
      }
    });
  }

  // ─────────────────────────────────────────────
  // Init
  // ─────────────────────────────────────────────

  function init() {
    // Header pill
    var header = document.getElementById('header-component');
    if (header) {
      // Make sure the no-transition guard is on (the inline pre-paint script
      // already added it; this is defensive in case the script ran before the
      // header was in the DOM).
      header.classList.add('dorie-no-transition');

      // Sync the pill state with the current scroll position.
      applyState(header);

      // Drop the no-transition guard after two frames so any state we just
      // applied is committed without animation, then transitions re-enable.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          header.classList.remove('dorie-no-transition');
        });
      });

      // Throttled scroll listener.
      window.addEventListener(
        'scroll',
        function () { onScroll(header); },
        { passive: true }
      );
    }

    // Announcement bar dismiss
    initAnnouncementDismiss();
  }

  // The script is deferred, so DOM is parsed by the time we run. But guard
  // anyway for environments where the script might be loaded differently.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
